/* eslint-disable security/detect-object-injection */
import './index.style.less'

import { ImportOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Col, Form, Row, Table, Typography } from 'antd'
import { useEffect, useState } from 'react'

import type { SortableContainerProps, SortEnd } from 'react-sortable-hoc'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import { arrayMoveImmutable } from 'array-move'

import FormItem from '../FormItem/FormItem'
import { ColumnsType } from 'antd/es/table/interface'
import { filter, orderBy } from 'lodash'
import { DataType, generateSizeColumn } from './measurement.column'
import {
  getRandomKey,
  tranformRequirementDataToFormData,
} from '../../EditOrder.helper'
import { isEmptyValue } from '@/utils/common'

const { Title } = Typography

const SortableItem = SortableElement(
  (props: React.HTMLAttributes<HTMLTableRowElement>) => <tr {...props} />
)
const SortableBody = SortableContainer(
  (props: React.HTMLAttributes<HTMLTableSectionElement>) => <tbody {...props} />
)

interface Props {
  title: string
  form: any
  data?: any
  sizes?: string[]
  orderSetting: any
  onClick: any
  renderHeader?: React.ReactNode
}

function Measurement(props: Props) {
  const { title, sizes = [], data } = props

  const [dataSource, setDataSource] = useState([])
  const [count, setCount] = useState(1)

  const [columns, setColumns] = useState<ColumnsType<DataType>>([])

  const mainKey = title.toLowerCase()

  useEffect(() => {
    const foundData = data?.filter((element) => element.data_type === 'json')
    if (foundData) {
      const temp = tranformRequirementDataToFormData(foundData[0].value)
      setDataSource(orderBy(temp, (val) => Number(val.index), ['asc']))
    }
  }, [data])

  const onSortEnd = ({ oldIndex, newIndex }: SortEnd) => {
    if (Number(oldIndex) !== Number(newIndex)) {
      const newData = arrayMoveImmutable(
        dataSource.slice(),
        oldIndex,
        newIndex
      ).filter((el: any) => !!el)
      setDataSource(newData)
    }
  }

  const handleRemovePOM = (key: string) => {
    const newData = filter(dataSource, (item) => {
      return item.field !== key
    })
    setDataSource(newData)
  }

  useEffect(() => {
    const columns = generateSizeColumn({
      sizes: sizes,
      handleRemovePOM: (key) => handleRemovePOM(key),
      key: mainKey,
      setFieldsValue: props.form.setFieldsValue,
    })
    setColumns(columns)
  }, [dataSource, sizes])

  function DraggableContainer(props: SortableContainerProps) {
    return (
      <SortableBody
        useDragHandle
        disableAutoscroll
        helperClass="row-dragging"
        onSortEnd={onSortEnd}
        {...props}
      />
    )
  }

  const handleAddPOM = () => {
    try {
      const newData: DataType = {
        field: getRandomKey(),
        index: count,
      }
      setDataSource([...dataSource, newData])
      setCount(count + 1)
    } catch (error) {
      console.error(error)
    }
  }

  // eslint-disable-next-line react/function-component-definition
  const DraggableBodyRow: React.FC<any> = ({ ...restProps }) => {
    // function findIndex base on Table rowKey props and should always be a right array index
    const index = dataSource.findIndex(
      (x) => x.index === restProps['data-row-key']
    )
    return <SortableItem index={index} {...restProps} />
  }

  const inputControl = (element) => {
    switch (element.data_type) {
      case 'json':
        return (
          <Table
            pagination={false}
            dataSource={dataSource}
            columns={columns}
            className="measurement-input-table"
            rowKey="index"
            scroll={{ x: 1500, y: 440 }}
            components={{
              body: {
                wrapper: DraggableContainer,
                row: DraggableBodyRow,
              },
            }}
          />
        )
      default:
        if (element.field === 'measurement_aql') return null
        
        return (
          <Col span={24} key={element.field}>
            <Row justify={'space-between'} gutter={50}>
              <Col span={12}>
                <FormItem orderSetting={props.orderSetting} item={element} />
              </Col>
              <Col span={12}>
                {props.renderHeader ? (
                  element.field === 'measurementUnit' ? props.renderHeader : null
                ) : (
                  <Row justify={'end'}>
                    {props.onClick && (
                      <Button
                        onClick={props.onClick}
                        icon={<ImportOutlined />}
                        size="large"
                        disabled={isEmptyValue(sizes)}
                        className={!isEmptyValue(sizes) ? 'import-excel-button' : ''}
                      >
                        Import from Excel
                      </Button>
                    )}
                  </Row>
                )}
              </Col>
            </Row>
          </Col>
        )
    }
  }

  return (
    <div className="measure-wrapper">
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 0 }}>
            {title}
          </Title>
        </Col>
        {props.renderHeader && props.onClick && (
          <Col>
            <Button
              type="text"
              onClick={props.onClick}
              icon={<ImportOutlined />}
              size="large"
              disabled={isEmptyValue(sizes)}
              className={!isEmptyValue(sizes) ? 'import-excel-button' : ''}
              style={{ color: '#8c8c8c' }}
            >
              Import from Excel
            </Button>
          </Col>
        )}
      </Row>
      <Form key={'key'} form={props.form} name={title}>
        {orderBy(props?.data, ['display_order'], ['asc'])?.map((element) => {
          return inputControl(element)
        })}
        <Col span={8}>
          <Button
            size="large"
            onClick={handleAddPOM}
            icon={<PlusOutlined />}
            block
          >
            Add a POM
          </Button>
        </Col>
      </Form>
    </div>
  )
}

export default Measurement
